<template>
  <div class="container-fluid search-page rfo-page rfo-page--standard oa-workspace">
    <div class="oa-page-toolbar">
      <router-link :to="{ name: 'pay-operations-order-amendments' }" class="oa-back-link">
        <i class="fa fa-arrow-left me-1"></i>Back to Queue
      </router-link>
      <h4 class="mb-0"><i class="fa fa-tags me-2"></i>Manual Order Assignment</h4>
    </div>
    <p class="text-muted">Add orders to an incident or override a classification.</p>

    <!-- Step indicator -->
    <div class="d-flex justify-content-center mb-4">
      <div class="d-flex align-items-center">
        <div v-for="(label, idx) in stepLabels" :key="label" class="d-flex align-items-center">
          <div
            class="rounded-circle d-flex align-items-center justify-content-center step-bubble"
            :class="idx <= currentStepIndex ? 'bg-primary text-white' : 'bg-light text-muted'"
          >
            {{ idx + 1 }}
          </div>
          <span
            class="ms-2 me-3 small"
            :class="idx <= currentStepIndex ? 'fw-bold' : 'text-muted'"
            >{{ label }}</span
          >
          <div v-if="idx < stepLabels.length - 1" class="border-top mx-2 step-divider"></div>
        </div>
      </div>
    </div>

    <StepTarget v-if="step === 'target'" @next="onTarget" />
    <StepInput v-else-if="step === 'input'" @next="onInput" @back="step = 'target'" />
    <StepReview
      v-else-if="step === 'review'"
      :uuids="uuids"
      :submitting="submitting"
      @back="step = 'input'"
      @submit="onSubmit"
    />
    <div v-else-if="step === 'result'">
      <div v-if="error" class="alert alert-danger outline">{{ error }}</div>
      <StepResult :session="session" />
      <div class="mt-3">
        <button class="btn btn-outline-secondary" @click="reset">Start Another</button>
        <router-link
          v-if="session"
          class="btn btn-link ms-2"
          :to="{
            name: 'pay-operations-manual-assignment-session',
            params: { sessionId: session.sessionId },
          }"
          >Open Shareable Link</router-link
        >
      </div>
    </div>

    <PayOpsUsageInstructions
      class="mt-4"
      description="Add orders to an incident or override an automatic classification by submitting one or more UUIDs. Conflicts are reported, never silently overwritten."
      :required-roles="manualAssignmentRequiredRoles"
      :workflow-notes="manualAssignmentWorkflowNotes"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import StepTarget from './components/StepTarget.vue';
import StepInput from './components/StepInput.vue';
import StepReview from './components/StepReview.vue';
import StepResult from './components/StepResult.vue';
import { useAssignmentSession, type AssignmentTarget } from './composables/useAssignmentSession';
import PayOpsUsageInstructions from '../shared/PayOpsUsageInstructions.vue';
import {
  PAY_OPS_MANUAL_ASSIGNMENT_ROLES,
  buildManualAssignmentWorkflowNotes,
} from '@/services/ryd-pay-operations/order-amendments/usage-instructions-config';

const manualAssignmentRequiredRoles = PAY_OPS_MANUAL_ASSIGNMENT_ROLES;
const manualAssignmentWorkflowNotes = buildManualAssignmentWorkflowNotes();

const step = ref<'target' | 'input' | 'review' | 'result'>('target');
const stepLabels = ['Choose Target', 'Paste UUIDs', 'Review', 'Result'];
const stepOrder = ['target', 'input', 'review', 'result'] as const;
const currentStepIndex = computed(() => stepOrder.indexOf(step.value));
const target = ref<AssignmentTarget | null>(null);
const reason = ref('');
const uuids = ref<string[]>([]);
const { submit, session, submitting, error } = useAssignmentSession();

function onTarget(payload: { target: AssignmentTarget; reason: string }) {
  target.value = payload.target;
  reason.value = payload.reason;
  step.value = 'input';
}
function onInput(parsed: string[]) {
  uuids.value = parsed;
  step.value = 'review';
}
async function onSubmit(finalUuids: string[]) {
  if (!target.value) return;
  try {
    await submit(target.value, reason.value, finalUuids);
    step.value = 'result';
  } catch {
    step.value = 'result';
  }
}
function reset() {
  target.value = null;
  reason.value = '';
  uuids.value = [];
  session.value = null;
  step.value = 'target';
}
</script>
